import { memo, useCallback, useDeferredValue, useMemo, useState } from "react";
import { useMutation } from "convex/react";
import { useQuery } from "@/lib/convex";
import {
  Box,
  Button,
  Checkbox,
  Field,
  Flex,
  HStack,
  Icon,
  Input,
  NativeSelect,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
} from "@chakra-ui/react";
import { createColumnHelper } from "@tanstack/react-table";
import { LuSend, LuSquareCheck, LuUsersRound } from "react-icons/lu";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import type { FunctionReturnType } from "convex/server";
import { TableSkeleton } from "../components/TableSkeleton";
import { DrawerSidebar } from "../components/DrawerSidebar";
import { SearchInput } from "../components/SearchInput";
import { DataTable } from "../components/DataTable";
import { FilterCount } from "../components/FilterCount";
import { SinglePersonPicker } from "../components/SinglePersonPicker";
import { toastError, toastSuccess } from "../components/ui/toaster";
import type { ActivitySummary, PersonOption } from "./ActivityAdminShared";

/** Rows put in the DOM at once. The filter is the way through a long list. */
const MAX_PERSON_ROWS = 50;

/**
 * Choose people and assign an activity to all of them.
 *
 * A component of its own, and that is the point rather than tidiness: every
 * piece of state here — the search box, the selection, the three date
 * overrides — belongs to this drawer and to nothing else. While it lived in
 * the page, typing one character re-rendered the assignments table behind it,
 * which is several hundred rows that could not have changed.
 *
 * State owned where it is used is the whole fix. No memo can repair a
 * component that re-renders because a sibling's text box changed.
 */

/**
 * The directory, as rows to tick.
 *
 * Memoised and given only what it draws. The drawer around it holds a chosen
 * activity and three date boxes, none of which change a single row -- and
 * before this, typing a date re-rendered fifty rows each carrying a checkbox,
 * which is a state machine apiece.
 */
const PersonPicker = memo(function PersonPicker({
  visiblePersons,
  totalMatching,
  hiddenCount,
  selectedIdSet,
  allFilteredSelected,
  onToggleOne,
  onToggleAll,
}: {
  visiblePersons: PersonOption[];
  totalMatching: number;
  hiddenCount: number;
  selectedIdSet: Set<string>;
  allFilteredSelected: boolean;
  onToggleOne: (personId: string) => void;
  onToggleAll: () => void;
}) {
  // The app's table, as everywhere else, with the ticks as its first column.
  // Selection stays the drawer's own list rather than the table's row state:
  // "all" means every match, including the rows past the cap below.
  const columns = useMemo(() => [
    personColumn.display({
      id: "select",
      header: () => (
        <Checkbox.Root size="sm" colorPalette="blue" checked={allFilteredSelected} onCheckedChange={onToggleAll}>
          <Checkbox.HiddenInput />
          <Checkbox.Control aria-label="Select every match" />
        </Checkbox.Root>
      ),
      cell: ({ row }) => {
        const personId = String(row.original._id);
        return (
          <Box onClick={(event) => event.stopPropagation()}>
            <Checkbox.Root size="sm" colorPalette="blue" checked={selectedIdSet.has(personId)} onCheckedChange={() => onToggleOne(personId)}>
              <Checkbox.HiddenInput />
              <Checkbox.Control aria-label={`Select ${row.original.fullName}`} />
            </Checkbox.Root>
          </Box>
        );
      },
    }),
    personColumn.accessor("fullName", {
      header: "Name",
      cell: ({ row }) => (
        <Stack gap={1}>
          <Text fontWeight="medium">{row.original.fullName}</Text>
          <Text fontSize="xs" color="fg.muted">{row.original.email}</Text>
        </Stack>
      ),
    }),
    personColumn.display({
      id: "group",
      header: "Group / role",
      cell: ({ row }) => {
        const person = row.original;
        return (
          <Stack gap={1} fontSize="xs" color="fg.muted">
            <Text>{person.primaryAffiliation?.orgUnit?.name ?? "No group"}</Text>
            <Text>{person.primaryAffiliation?.positionLabel ?? "No position listed"}</Text>
            {person.enrollment?.cohort ? <Text color="blue.500">Cohort {person.enrollment.cohort}</Text> : null}
          </Stack>
        );
      },
    }),
  ], [allFilteredSelected, onToggleAll, onToggleOne, selectedIdSet]);

  return (
    // Its own scroll box, so the header pins to it rather than to the page.
    <Box mt={3} minH={0} flex="1" overflow="auto" borderWidth="1px" borderColor="border" bg="bg">
      <DataTable
        data={visiblePersons}
        columns={columns}
        getRowId={getPersonRowId}
        onRowClick={(person) => onToggleOne(String(person._id))}
        emptyMessage="No people match the search."
        stickyHeader
        size="sm"
      />
      {/* Said plainly, because a truncated list that looks complete is
          worse than a long one: selecting all still takes every match. */}
      {hiddenCount > 0 ? (
        <Text px={4} py={3} fontSize="xs" color="fg.muted">
          Showing the first {visiblePersons.length} of {totalMatching}. Narrow the search to
          see the rest; selecting all still covers every match.
        </Text>
      ) : null}
    </Box>
  );
});

const personColumn = createColumnHelper<PersonOption>();
const getPersonRowId = (person: PersonOption) => String(person._id);

type PrerequisiteRow = FunctionReturnType<typeof api.personActivities.missingPrerequisites>[number];

/**
 * What the chosen activity is completed after, each named once, with how many
 * of the selected people do not have it yet.
 *
 * Orange only when somebody is missing one: then those copies cannot be
 * completed until it is assigned to them, which is worth stopping for.
 */
function PrerequisitesNotice({
  prerequisites,
  selectedCount,
}: {
  prerequisites: PrerequisiteRow[];
  selectedCount: number;
}) {
  const anyMissing = selectedCount > 0 && prerequisites.some((row) => row.missingCount > 0);
  return (
    <Stack
      gap={2}
      borderWidth="1px"
      borderColor={anyMissing ? "orange.muted" : "border"}
      bg={anyMissing ? "orange.subtle" : "bg.muted"}
      px={4}
      py={3}
      fontSize="sm"
    >
      <Text>This activity is completed after:</Text>
      <Stack as="ul" gap={1} pl={4} listStyleType="disc">
        {prerequisites.map((row) => (
          <li key={row.activityId}>
            {row.title}
            {selectedCount === 0 ? null : (
              <Text as="span" color="fg.muted">
                {" — "}
                {row.missingCount === 0
                  ? "everyone selected has it"
                  : `${row.missingCount} of the ${selectedCount} selected ${row.missingCount === 1 ? "does" : "do"} not have it`}
              </Text>
            )}
          </li>
        ))}
      </Stack>
      {anyMissing ? (
        <Text fontSize="xs" color="fg.muted">
          Their copies of this activity cannot be completed until the missing ones are assigned to them.
        </Text>
      ) : null}
    </Stack>
  );
}

export function AssignActivityDrawer({
  open,
  onClose,
  catalogue,
  persons,
}: {
  open: boolean;
  onClose: () => void;
  catalogue: ActivitySummary[] | undefined;
  persons: PersonOption[] | undefined;
}) {
  const assignActivityToPersons = useMutation(api.personActivities.assignActivityToPersons);

  const [personSearch, setPersonSearch] = useState("");
  // The box repaints on the keystroke; the list catches up a frame later.
  const deferredPersonSearch = useDeferredValue(personSearch);
  const [selectedActivityId, setSelectedActivityId] = useState<string>("");
  const [dueDay, setDueDay] = useState("");
  const [startDay, setStartDay] = useState("");
  const [opensOnDay, setOpensOnDay] = useState("");
  const [anchorEventId, setAnchorEventId] = useState("");

  const anchorableEvents = useQuery(api.events.listAnchorableEvents, open ? {} : "skip");

  // An `eventDate` rule measures from the occasion the copy is for, so
  // without one that date resolves to null and the copy quietly carries no
  // deadline at all. Any of the three rules may use it.
  const needsAnEvent = useMemo(() => {
    const activity = (catalogue ?? []).find((row) => String(row._id) === selectedActivityId);
    return [activity?.recommendedStartRule, activity?.opensOnRule, activity?.dueRule]
      .some((rule) => rule != null && rule.anchor === "eventDate");
  }, [catalogue, selectedActivityId]);
  const [selectedPersonIds, setSelectedPersonIds] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // The steps that name somebody when assigned, and who that is. One person
  // for everybody in this assignment; a different one means assigning again.
  const namedSteps = useMemo(
    () => (catalogue ?? []).find((row) => String(row._id) === selectedActivityId)?.namedAtAssignment ?? [],
    [catalogue, selectedActivityId],
  );
  const [namedPersonId, setNamedPersonId] = useState<string | null>(null);

  // What the chosen activity waits on, and how many of the selected people
  // lack each. Assigned without its prerequisite, an activity waits for one
  // nobody may ever assign, so the drawer says so. It does not assign them:
  // that stays a decision, made on its own.
  const prerequisites = useQuery(
    api.personActivities.missingPrerequisites,
    open && selectedActivityId !== ""
      ? {
          activityId: selectedActivityId as Id<"activities">,
          personIds: selectedPersonIds as Id<"persons">[],
        }
      : "skip",
  );

  function handleActivityChange(nextId: string) {
    setSelectedActivityId(nextId);
    setNamedPersonId(null);
    // Deliberately no prefill. Dates come from the activity's own rules now,
    // resolved per person at assignment (model §8); filling these boxes would
    // turn every assignment into a hand-set override of its rule.
    setDueDay("");
    setStartDay("");
    setOpensOnDay("");
    setAnchorEventId("");
  }

  const filteredPersons = useMemo(() => {
    const query = deferredPersonSearch.trim().toLowerCase();
    return (persons ?? []).filter((person) => {
      if (query.length === 0) {
        return true;
      }

      return [
        person.fullName,
        person.email,
        person.primaryAffiliation?.orgUnit?.name ?? "",
        person.primaryAffiliation?.positionLabel ?? "",
        person.shortName ?? "",
        person.enrollment?.cohort ? `cohort ${person.enrollment.cohort}` : "",
      ].some((value) => (value ?? "").toLowerCase().includes(query));
    });
  }, [deferredPersonSearch, persons]);

  // A set, not an array scan: this is asked once per rendered row, and the
  // array version made selecting somebody cost the length of the list
  // times the length of the selection.
  const selectedIdSet = useMemo(() => new Set(selectedPersonIds), [selectedPersonIds]);
  const allFilteredSelected = filteredPersons.length > 0
    && filteredPersons.every((person) => selectedIdSet.has(String(person._id)));

  // Only a screenful reaches the DOM. Each row carries a checkbox, and a
  // Chakra checkbox is a state machine -- three hundred of them re-rendered
  // on every keystroke is where the delay came from. Selecting all still
  // covers every match, not just the ones on screen, so the bulk flow the
  // page exists for is untouched.
  const visiblePersons = useMemo(
    () => filteredPersons.slice(0, MAX_PERSON_ROWS),
    [filteredPersons],
  );
  const hiddenPersonCount = filteredPersons.length - visiblePersons.length;

  // Stable, or the memo on the picker buys nothing: a new function is a
  // changed prop, and a changed prop is a re-render of every row.
  const togglePersonSelection = useCallback((personId: string) => {
    setSelectedPersonIds((current) => current.includes(personId)
      ? current.filter((entry) => entry !== personId)
      : [...current, personId]);
  }, []);

  const toggleSelectAllFiltered = useCallback(() => {
    setSelectedPersonIds((current) => {
      const filteredIds = filteredPersons.map((person) => String(person._id));
      if (filteredIds.every((id) => current.includes(id))) {
        return current.filter((id) => !filteredIds.includes(id));
      }
      return [...new Set([...current, ...filteredIds])];
    });
  }, [filteredPersons]);

  async function handleBulkAssign() {
    if (!selectedActivityId) {
      toastError("Select a activity before assigning activities.");
      return;
    }

    if (selectedPersonIds.length === 0) {
      toastError("Select at least one CeMMie to assign the activity to.");
      return;
    }

    if (namedSteps.length > 0 && namedPersonId === null) {
      toastError("Choose who is named when assigned: some steps of this activity are theirs.");
      return;
    }

    // Refused rather than assigned with a date that can never resolve: the
    // copy would carry no deadline and nothing would say why.
    if (needsAnEvent && anchorEventId === "") {
      toastError("Choose the occasion this is for: one of its dates is measured from it.");
      return;
    }

    setIsSubmitting(true);
    try {
      await assignActivityToPersons({
        activityId: selectedActivityId as Id<"activities">,
        personIds: selectedPersonIds as Id<"persons">[],
        anchorEventId: anchorEventId === "" ? null : (anchorEventId as Id<"events">),
        // Empty means "use the activity's rule"; a value overrides it.
        dueDay: dueDay === "" ? null : dueDay,
        recommendedStartDay: startDay === "" ? null : startDay,
        opensOnDay: opensOnDay === "" ? null : opensOnDay,
        namedPersonId: namedSteps.length > 0 ? (namedPersonId as Id<"persons">) : null,
      });
      toastSuccess(`Assigned activity to ${selectedPersonIds.length} ${selectedPersonIds.length === 1 ? "person" : "people"}.`);
      setSelectedPersonIds([]);
      setSelectedActivityId("");
      setNamedPersonId(null);
      setDueDay("");
      setStartDay("");
      setAnchorEventId("");
      setPersonSearch("");
      onClose();
    } catch (error) {
      toastError(error instanceof Error ? error.message : "Bulk assignment failed.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
  <DrawerSidebar
    open={open}
    onClose={() => onClose()}
    title="Assign to CeMMies"
    subtitle="Activity assignment"
    // The snapshot note belongs with what the drawer does, not between the
    // occasion and the dates, where it read as a remark about either.
    description="Assign an activity to several people at once. Later changes to the activity don't affect copies already assigned."
    icon={<Icon as={LuUsersRound} boxSize={5} />}
    footer={
      <HStack gap={3} w="full">
        <Button colorPalette="blue" flex="1" disabled={isSubmitting} onClick={() => void handleBulkAssign()}>
          <LuSend />
          {isSubmitting ? "Assigning…" : `Assign to ${selectedPersonIds.length} ${selectedPersonIds.length === 1 ? "person" : "people"}`}
        </Button>
        <Button variant="outline" onClick={() => onClose()}>
          Cancel
        </Button>
      </HStack>
    }
  >
    {/* Assignment details */}
    <Stack gap={3} flexShrink={0} borderBottomWidth="1px" borderColor="border" px={6} py={5}>
      <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="0.18em" color="fg.muted">Assignment details</Text>
      <Field.Root>
        <Field.Label>Activity</Field.Label>
        <NativeSelect.Root>
          <NativeSelect.Field value={selectedActivityId} onChange={(event) => handleActivityChange(event.target.value)}>
            <option value="">Select a activity…</option>
            {(catalogue ?? []).map((activity) => (
              <option key={activity._id} value={activity._id}>
                {activity.title} · {activity.required ? "required" : "optional"}
              </option>
            ))}
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
      </Field.Root>
      {namedSteps.length > 0 ? (
        <Field.Root required invalid={namedPersonId === null}>
          <SinglePersonPicker
            label="Named when assigned"
            people={persons ?? []}
            selectedPersonId={namedPersonId}
            onChange={setNamedPersonId}
          />
          <Field.HelperText color={namedPersonId === null ? "fg.error" : undefined}>
            {namedSteps.map((step) => {
              const what = step.does && step.signs ? "does and signs off" : step.does ? "does" : "signs off";
              return `${what[0]!.toUpperCase()}${what.slice(1)} step ${step.order}, "${step.title}".`;
            }).join(" ")}
            {" "}The same person for everybody selected.
          </Field.HelperText>
        </Field.Root>
      ) : null}
      {prerequisites && prerequisites.length > 0 ? (
        <PrerequisitesNotice prerequisites={prerequisites} selectedCount={selectedPersonIds.length} />
      ) : null}
      {/* The occasion a copy is made FOR (model §6, link 1). Offered on every
          assignment because binding a copy to an occasion is useful on its own
          — a cancelled occasion cancels the work made for it, whether or not
          any date came from it — and REQUIRED where a date rule measures from
          an event, since without one that date can never resolve. */}
      <Field.Root required={needsAnEvent} invalid={needsAnEvent && anchorEventId === ""}>
        <Field.Label>
          For which occasion
          {/* The asterisk where it is needed, the word where it is not. */}
          <Field.RequiredIndicator
            fallback={<Text as="span" ml={1} fontSize="xs" color="fg.muted">optional</Text>}
          />
        </Field.Label>
        <NativeSelect.Root>
          <NativeSelect.Field
            value={anchorEventId}
            onChange={(event) => setAnchorEventId(event.target.value)}
          >
            <option value="">Not for any particular occasion</option>
            {(anchorableEvents ?? []).map((event) => (
              <option key={event._id} value={event._id}>
                {event.startDay} — {event.title}
              </option>
            ))}
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
        {needsAnEvent ? (
          <Field.HelperText color={anchorEventId === "" ? "fg.error" : undefined}>
            This activity dates something from the occasion it is for, so it needs one.
          </Field.HelperText>
        ) : (
          <Field.HelperText>
            Binds the copies to that occasion: if it moves their dates follow, and if it is
            cancelled they are cancelled with it.
          </Field.HelperText>
        )}
      </Field.Root>
      <SimpleGrid gap={4} columns={{ base: 1, md: 3 }}>
        <Field.Root>
          <Field.Label>Due date</Field.Label>
          <Input type="date" value={dueDay} onChange={(event) => setDueDay(event.target.value)} />
        </Field.Root>
        <Field.Root>
          <Field.Label>Suggested start</Field.Label>
          <Input type="date" value={startDay} onChange={(event) => setStartDay(event.target.value)} />
        </Field.Root>
        <Field.Root>
          <Field.Label>Opens on</Field.Label>
          <Input type="date" value={opensOnDay} onChange={(event) => setOpensOnDay(event.target.value)} />
        </Field.Root>
      </SimpleGrid>
      <Text fontSize="xs" color="fg.muted">
        Leave these empty to use the activity&rsquo;s own rules, resolved per person.
        A suggested start is advice; &ldquo;opens on&rdquo; is a gate.
      </Text>
    </Stack>

    {/* People section. Never shorter than a handful of rows: with no floor
        it shrank to nothing on a short window, the table under the footer
        and the body scrolling only as far as the fields above it. With one,
        the whole body scrolls instead. */}
    <Flex minH="26rem" flex="1" direction="column" px={6} py={5}>
      <Text flexShrink={0} fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="0.18em" color="fg.muted">Select people</Text>

      <SearchInput
        mt={3}
        flexShrink={0}
        placeholder="Search by name, email, group, cohort"
        value={personSearch}
        onChange={setPersonSearch}
      />

      {/* The table's count, as on every other table, with what is ticked
          beside it: a search narrows the list, never the selection. */}
      <Flex mt={2} flexShrink={0} flexWrap="wrap" align="center" justify="space-between" gap={3}>
        {/* The directory is fetched when the drawer opens, so it can be a
            moment behind it. */}
        {persons === undefined ? (
          <Skeleton height="4" width="10rem" />
        ) : (
          <FilterCount
            shown={filteredPersons.length}
            total={persons.length}
            noun={["person", "people"]}
            filtered={personSearch.trim() !== ""}
            onClear={() => setPersonSearch("")}
          />
        )}
        <HStack gap={3}>
          <HStack gap={1.5} fontSize="sm" color="fg.muted">
            <Icon as={LuSquareCheck} boxSize={4} color="blue.500" />
            {selectedPersonIds.length} selected
          </HStack>
          <Button size="xs" variant="outline" onClick={toggleSelectAllFiltered} disabled={filteredPersons.length === 0}>
            {allFilteredSelected ? "Unselect these" : "Select all these"}
          </Button>
        </HStack>
      </Flex>

      {persons === undefined ? (
        <TableSkeleton label="Loading people" rows={8} columns={4} />
      ) : (
        <PersonPicker
          visiblePersons={visiblePersons}
          totalMatching={filteredPersons.length}
          hiddenCount={hiddenPersonCount}
          selectedIdSet={selectedIdSet}
          allFilteredSelected={allFilteredSelected}
          onToggleOne={togglePersonSelection}
          onToggleAll={toggleSelectAllFiltered}
        />
      )}
    </Flex>
  </DrawerSidebar>
  );
}
